/**
 * RealtimeManager - Unified Channel Management (채널 CRUD만)
 *
 * ISSUE_047: Centralized Realtime channel registry and lifecycle management
 * ISSUE_073: 자가 복구 제거 — 복구는 Coordinator가 전담
 *
 * Key Features:
 * - Central registry for all realtime channels
 * - Zombie channel cleanup (30s timeout)
 * - 채널 CRUD만 담당 (자가 복구 X, 자가 resetAll X)
 *
 * @module core/realtime/RealtimeManager
 */

import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase, isSupabaseConfigured } from '~/shared/lib/supabase';
import { touchRealtimeEvent } from '~/core/resilience/HealthMonitor';

// =============================================================================
// TYPES
// =============================================================================

export interface ChannelConfig {
  name: string;
  event: '*' | 'INSERT' | 'UPDATE' | 'DELETE';
  schema: string;
  table: string;
  filter?: string;
  onPayload: (payload: unknown) => void;
  onStatusChange?: (status: string, err?: Error) => void;
}

interface ManagedChannel {
  config: ChannelConfig;
  channel: RealtimeChannel | null;
  createdAt: number;
  lastActivityAt: number;
  subscribeCount: number;
}

export type RealtimeManagerEventType =
  | 'channel_subscribed'
  | 'channel_unsubscribed'
  | 'channel_error'
  | 'all_channels_reset'
  | 'zombie_cleaned';

export type RealtimeManagerEventHandler = (event: {
  type: RealtimeManagerEventType;
  channelName?: string;
  reason?: string;
}) => void;

// =============================================================================
// CONSTANTS
// =============================================================================

/** Zombie channel timeout (30 seconds) */
const ZOMBIE_TIMEOUT_MS = 30 * 1000;

/** Cleanup interval (60 seconds) */
const CLEANUP_INTERVAL_MS = 60 * 1000;

// =============================================================================
// REALTIME MANAGER
// =============================================================================

/** StrictMode 재등록 대기 시간 — 이 시간 안에 re-register되면 제거 취소 */
const UNREGISTER_DELAY_MS = 200;

class RealtimeManager {
  private channels: Map<string, ManagedChannel> = new Map();
  private configs: Map<string, ChannelConfig> = new Map();
  private eventHandlers: RealtimeManagerEventHandler[] = [];
  private cleanupTimer: ReturnType<typeof setInterval> | null = null;
  private pendingRemovals: Map<string, ReturnType<typeof setTimeout>> = new Map();
  private isResetting = false;
  private isInitialized = false;

  /**
   * Initialize the RealtimeManager
   * ISSUE_073: AuthBroker 구독, visibility 리스너 제거 — Coordinator가 전담
   */
  initialize(): void {
    if (this.isInitialized) return;

    if (!isSupabaseConfigured() || !supabase) return;

    this.isInitialized = true;

    // Start zombie cleanup timer
    this.startCleanupTimer();
  }

  /**
   * Stop the RealtimeManager
   */
  stop(): void {
    if (!this.isInitialized) return;

    // Stop cleanup timer
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
    }

    // Pending removals 정리
    for (const timer of this.pendingRemovals.values()) {
      clearTimeout(timer);
    }
    this.pendingRemovals.clear();

    // Unsubscribe all channels
    this.unsubscribeAllChannels();

    this.channels.clear();
    this.configs.clear();
    this.eventHandlers = [];
    this.isInitialized = false;
  }

  /**
   * Register a channel configuration
   * The channel will be created and subscribed automatically
   */
  register(config: ChannelConfig): () => void {
    if (!supabase) return () => {};

    // StrictMode 지연 제거 취소 — re-register가 먼저 왔으면 제거하지 않음
    const pendingTimer = this.pendingRemovals.get(config.name);
    if (pendingTimer) {
      clearTimeout(pendingTimer);
      this.pendingRemovals.delete(config.name);
    }

    const existing = this.channels.get(config.name);
    if (existing) {
      // Update config and increment subscribe count
      existing.config = config;
      existing.subscribeCount++;
      this.configs.set(config.name, config);
      return () => this.unregister(config.name);
    }

    // Store config
    this.configs.set(config.name, config);

    // Create and subscribe channel
    const channel = this.createChannel(config);

    const managed: ManagedChannel = {
      config,
      channel,
      createdAt: Date.now(),
      lastActivityAt: Date.now(),
      subscribeCount: 1,
    };

    this.channels.set(config.name, managed);

    return () => this.unregister(config.name);
  }

  /**
   * Unregister a channel
   */
  unregister(name: string): void {
    const managed = this.channels.get(name);
    if (!managed) return;

    managed.subscribeCount--;

    if (managed.subscribeCount <= 0) {
      // 지연 제거: React StrictMode가 즉시 re-register할 수 있으므로
      // 짧은 딜레이 후에도 재등록이 없으면 실제 제거
      const timer = setTimeout(() => {
        this.pendingRemovals.delete(name);
        const current = this.channels.get(name);
        // 딜레이 중 re-register 됐으면 제거하지 않음
        if (current && current.subscribeCount > 0) return;

        if (current?.channel && supabase) {
          supabase.removeChannel(current.channel).catch((err) => {
            console.error(`[RealtimeManager] Error removing channel ${name}:`, err);
          });
        }

        this.channels.delete(name);
        this.configs.delete(name);

        this.emit({ type: 'channel_unsubscribed', channelName: name });
      }, UNREGISTER_DELAY_MS);

      this.pendingRemovals.set(name, timer);
    }
  }

  /**
   * Reset all channels (full reconnection)
   * WebSocket 자체를 재연결한 후 채널 재구독
   * ISSUE_073: Coordinator에서만 호출
   */
  async resetAll(reason: string): Promise<void> {
    if (this.isResetting) return;

    if (this.channels.size === 0) return;

    this.isResetting = true;

    try {
      // 1. Unsubscribe all existing channels
      await this.unsubscribeAllChannels();

      // 2. WebSocket 자체 재연결 (핵심!)
      if (supabase) {
        try {
          supabase.realtime.disconnect();
          await new Promise(resolve => setTimeout(resolve, 200));
          await supabase.realtime.connect();
        } catch (wsError) {
          console.error('[RealtimeManager] WebSocket reconnect failed:', wsError);
        }
      }

      // 3. Wait for connection stabilization
      await new Promise(resolve => setTimeout(resolve, 300));

      // 4. Recreate all channels from configs
      for (const [name, config] of this.configs) {
        const channel = this.createChannel(config);
        const managed = this.channels.get(name);
        if (managed) {
          managed.channel = channel;
          managed.lastActivityAt = Date.now();
        } else {
          this.channels.set(name, {
            config,
            channel,
            createdAt: Date.now(),
            lastActivityAt: Date.now(),
            subscribeCount: 1,
          });
        }
      }

      this.emit({ type: 'all_channels_reset', reason });
    } catch (error) {
      console.error('[RealtimeManager] Reset failed:', error);
    } finally {
      this.isResetting = false;
    }
  }

  /**
   * Get channel by name
   */
  getChannel(name: string): RealtimeChannel | null {
    return this.channels.get(name)?.channel ?? null;
  }

  /**
   * Check if a channel is registered
   */
  hasChannel(name: string): boolean {
    return this.channels.has(name);
  }

  /**
   * Get all channel names
   */
  getChannelNames(): string[] {
    return Array.from(this.channels.keys());
  }

  /**
   * Register event handler
   */
  onEvent(handler: RealtimeManagerEventHandler): () => void {
    this.eventHandlers.push(handler);
    return () => {
      this.eventHandlers = this.eventHandlers.filter(h => h !== handler);
    };
  }

  /**
   * Touch a channel's activity (call when payload received)
   */
  touchChannel(name: string): void {
    const managed = this.channels.get(name);
    if (managed) {
      managed.lastActivityAt = Date.now();
    }
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  /**
   * Create a channel from config
   * ISSUE_073: 채널 타임아웃 시 channel_error 이벤트 emit
   */
  private createChannel(config: ChannelConfig): RealtimeChannel {
    if (!supabase) {
      throw new Error('Supabase not configured');
    }

    const channel = supabase
      .channel(config.name)
      .on(
        'postgres_changes',
        {
          event: config.event,
          schema: config.schema,
          table: config.table,
          ...(config.filter && { filter: config.filter }),
        },
        (payload) => {
          // Touch health monitor
          touchRealtimeEvent();

          // Touch channel activity
          this.touchChannel(config.name);

          // Call user callback
          config.onPayload(payload);
        }
      )
      .subscribe((status, err) => {
        if (config.onStatusChange) {
          config.onStatusChange(status, err);
        }

        if (status === 'SUBSCRIBED') {
          this.emit({ type: 'channel_subscribed', channelName: config.name });
        } else if (status === 'CHANNEL_ERROR') {
          // ISSUE_155: console 로깅은 Coordinator → ResilienceReporter 일원화.
          // 여기서는 emit 만 — Reporter 가 윈도우 상태에 따라 버퍼/즉시 error 판정.
          this.emit({ type: 'channel_error', channelName: config.name, reason: err?.message ?? 'Unknown error' });
        } else if (status === 'TIMED_OUT') {
          this.handleChannelTimeout(config.name);
        }
      });

    return channel;
  }

  /**
   * Unsubscribe all channels
   */
  private async unsubscribeAllChannels(): Promise<void> {
    if (!supabase) return;

    const promises = Array.from(this.channels.values()).map(async (managed) => {
      if (managed.channel) {
        try {
          await supabase!.removeChannel(managed.channel);
          managed.channel = null;
        } catch (err) {
          console.error(`[RealtimeManager] Error removing channel ${managed.config.name}:`, err);
        }
      }
    });

    await Promise.all(promises);
  }

  /**
   * Handle channel timeout
   * ISSUE_073: 채널 제거 + channel_error 이벤트 emit → Coordinator가 복구
   */
  private handleChannelTimeout(name: string): void {
    const managed = this.channels.get(name);
    if (!managed) return;

    // 채널 정리 (config는 유지 → resetAll 시 재생성 가능)
    if (managed.channel && supabase) {
      supabase.removeChannel(managed.channel).catch(() => {});
      managed.channel = null;
    }

    // ISSUE_073: Coordinator에게 알림
    this.emit({ type: 'channel_error', channelName: name, reason: 'TIMED_OUT' });
  }

  /**
   * Start zombie cleanup timer
   */
  private startCleanupTimer(): void {
    this.cleanupTimer = setInterval(() => {
      this.cleanupZombieChannels();
    }, CLEANUP_INTERVAL_MS);
  }

  /**
   * Clean up zombie channels (inactive for too long)
   */
  private cleanupZombieChannels(): void {
    const now = Date.now();
    const zombies: string[] = [];

    for (const [name, managed] of this.channels) {
      const inactiveTime = now - managed.lastActivityAt;

      // Channel is zombie if inactive and has no subscribers
      if (inactiveTime > ZOMBIE_TIMEOUT_MS && managed.subscribeCount <= 0) {
        zombies.push(name);
      }
    }

    if (zombies.length > 0) {
      for (const name of zombies) {
        const managed = this.channels.get(name);
        if (managed?.channel && supabase) {
          supabase.removeChannel(managed.channel).catch(() => {});
        }
        this.channels.delete(name);
        this.configs.delete(name);

        this.emit({ type: 'zombie_cleaned', channelName: name });
      }
    }
  }

  /**
   * Emit event to handlers
   */
  private emit(event: {
    type: RealtimeManagerEventType;
    channelName?: string;
    reason?: string;
  }): void {
    this.eventHandlers.forEach(handler => {
      try {
        handler(event);
      } catch (err) {
        console.error('[RealtimeManager] Event handler error:', err);
      }
    });
  }
}

// =============================================================================
// SINGLETON EXPORT
// =============================================================================

export const realtimeManager = new RealtimeManager();

export function initializeRealtimeManager(): void {
  realtimeManager.initialize();
}

export function stopRealtimeManager(): void {
  realtimeManager.stop();
}

export function registerRealtimeChannel(config: ChannelConfig): () => void {
  return realtimeManager.register(config);
}

export function unregisterRealtimeChannel(name: string): void {
  realtimeManager.unregister(name);
}

export function resetAllRealtimeChannels(reason: string): Promise<void> {
  return realtimeManager.resetAll(reason);
}

export function getRealtimeChannel(name: string): RealtimeChannel | null {
  return realtimeManager.getChannel(name);
}
